import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import {
  ProductListItemFulfillmentMode,
  ProductStatus,
  SchedulePlacementStateInput,
  StoreProductStatus,
  type Attribute,
  type StaffProfileStoresItem,
} from '@nucleus-commerce/api-client/admin';

import { productTypesQueryOptions } from '@/features/dictionary';
import { BASE_CURRENCY } from '@/shared/catalog/base-currency';

import { resourcePickersApi } from '../api/resource-pickers-api';
import { attributeFilterKey, type ProductPickerScope } from '../lib/product-filters';

/** Rows per page; the picker loads the next page as the list scrolls. */
export const PICKER_PAGE_SIZE = 30;

/** Attribute types the lists filter on in v1 (decision 4): equals any of the value codes. */
const CHOICE_TYPES = new Set<Attribute['dataType']>(['select', 'multiselect']);

const STOCK_VALUES = ['in_stock', 'out_of_stock', 'low_stock'] as const;

export interface ProductFilterDefsOptions {
  /** Any `t`; keys are namespaced (`resourcePickers:`, `common:`). */
  t: TFunction;
  queryClient: QueryClient;
  /** The session's stores: In store / Not in store options and, in org scope, where collections live. */
  stores: ReadonlyArray<Pick<StaffProfileStoresItem, 'id' | 'name'>>;
  /** The attribute dictionary; filterable choice and yes/no attributes get a chip each. */
  attributes?: readonly Attribute[];
  /** Filter keys to leave out: `status` when the caller pins statuses, say. */
  omit?: readonly string[];
}

const matches = (needle: string, label: string) =>
  !needle.trim() || label.toLowerCase().includes(needle.trim().toLowerCase());

/** Options for a tag or vendor: the value is the text itself, so labels resolve without a call. */
const textOptions = (values: readonly string[]): FilterOption[] =>
  values.map((value) => ({ value, label: value }));

/**
 * Every product filter of D-182 decision 4 for a scope, keyed by the list endpoints' query params
 * (`toProductListParams` and `toProductFilters` read the same keys). Used by the product picker
 * and, so lists and picker show the same filters, by the Products and Store catalog lists.
 *
 * Org scope: status, product type, collection (any of the organization's) and "in no collection",
 * tags (any / all), vendor, price, stock, in store / not in store / in any store, needs attention,
 * created, updated, image, scheduled, fulfillment, attributes. Store scope swaps in store status,
 * published, schedule state and schedule, unpriced, and the store's own collections.
 */
export function productFilterDefs<TData = never>(
  scope: ProductPickerScope,
  { t, queryClient, stores, attributes = [], omit = [] }: ProductFilterDefsOptions,
): Array<FilterDef<TData>> {
  const productTypes = async () =>
    (await queryClient.query(productTypesQueryOptions())).map((type): FilterOption => ({
      value: type.id,
      label: type.name,
    }));
  const storeOptions = stores.map((s): FilterOption => ({ value: s.id, label: s.name }));
  // Store scope: that store's collections. Org scope: a collection of any store.
  const collectionStores = scope.kind === 'store' ? [{ id: scope.storeId, name: '' }] : stores;
  const collections = async (q: string, limit: number, signal: AbortSignal) =>
    (
      await Promise.all(
        collectionStores.map(async (store) => {
          const page = await resourcePickersApi.collections(
            store.id,
            { limit, ...(q ? { q } : {}) },
            signal,
          );
          return page.items.map((c): FilterOption => ({
            value: c.id,
            label: c.title,
            ...(store.name ? { description: store.name } : {}),
          }));
        }),
      )
    ).flat();
  const yesNo = (key: string) => ({
    trueLabel: t(`resourcePickers:filters.${key}Yes`),
    falseLabel: t(`resourcePickers:filters.${key}No`),
  });

  const defs: Array<FilterDef<TData>> = [
    scope.kind === 'org'
      ? {
          key: 'status',
          label: t('resourcePickers:filters.status'),
          type: 'enum',
          negatable: true,
          pinned: true,
          options: Object.values(ProductStatus).map((value) => ({
            value,
            label: t(`common:status.${value}`),
          })),
        }
      : {
          key: 'status',
          label: t('resourcePickers:filters.storeStatus'),
          type: 'enum',
          negatable: true,
          pinned: true,
          options: Object.values(StoreProductStatus).map((value) => ({
            value,
            label: t(`common:status.${value}`),
          })),
        },
    {
      key: 'productTypeId',
      label: t('resourcePickers:filters.productType'),
      type: 'async',
      pinned: true,
      aliases: ['type'],
      debounceMs: 0,
      loadOptions: async (q) => (await productTypes()).filter((o) => matches(q, o.label)),
      resolveOptions: async (values) =>
        (await productTypes()).filter((o) => values.includes(o.value)),
    },
    {
      key: 'collectionId',
      label: t('resourcePickers:filters.collection'),
      type: 'async',
      pinned: true,
      aliases: ['collection'],
      loadOptions: (q, signal) => collections(q.trim(), 20, signal),
      resolveOptions: async (values, signal) =>
        (await collections('', 100, signal)).filter((o) => values.includes(o.value)),
    },
    {
      key: 'collectionId_none',
      label: t('resourcePickers:filters.collectionNone'),
      type: 'boolean',
      ...yesNo('collectionNone'),
    },
    {
      key: 'tags',
      label: t('resourcePickers:filters.tags'),
      type: 'async',
      aliases: ['tag'],
      loadOptions: async (q, signal) =>
        textOptions((await resourcePickersApi.tags(q.trim(), signal)).items.map((i) => i.value)),
      resolveOptions: (values) => Promise.resolve(textOptions(values)),
    },
    {
      key: 'tagsAll',
      label: t('resourcePickers:filters.tagsAll'),
      type: 'async',
      loadOptions: async (q, signal) =>
        textOptions((await resourcePickersApi.tags(q.trim(), signal)).items.map((i) => i.value)),
      resolveOptions: (values) => Promise.resolve(textOptions(values)),
    },
    {
      key: 'vendor',
      label: t('resourcePickers:filters.vendor'),
      type: 'async',
      loadOptions: async (q, signal) =>
        textOptions((await resourcePickersApi.vendors(q.trim(), signal)).items.map((i) => i.value)),
      resolveOptions: (values) => Promise.resolve(textOptions(values)),
    },
    {
      key: 'price',
      label: t('resourcePickers:filters.price'),
      type: 'number-range',
      min: 0,
      // The filter matches a variant's base price, which is in the organization's base currency
      // in both scopes (a store-resolved price filter is deferred, D-182).
      currency: BASE_CURRENCY,
    },
  ];

  defs.push({
    key: 'stock',
    label: t('resourcePickers:filters.stock'),
    type: 'enum',
    options: STOCK_VALUES.map((value) => ({
      value,
      label: t(`resourcePickers:filters.stockValue.${value}`),
    })),
  });
  if (scope.kind === 'org') {
    defs.push(
      {
        key: 'inStoreId',
        label: t('resourcePickers:filters.inStore'),
        type: 'enum',
        multiple: false,
        aliases: ['store'],
        options: storeOptions,
      },
      {
        key: 'notInStoreId',
        label: t('resourcePickers:filters.notInStore'),
        type: 'enum',
        multiple: false,
        options: storeOptions,
      },
      {
        key: 'inAnyStore',
        label: t('resourcePickers:filters.inAnyStore'),
        type: 'boolean',
        ...yesNo('inAnyStore'),
      },
      {
        key: 'needsAttention',
        label: t('resourcePickers:filters.needsAttention'),
        type: 'boolean',
        ...yesNo('needsAttention'),
      },
    );
  }

  defs.push(
    {
      key: 'createdAt',
      label: t(
        scope.kind === 'store'
          ? 'resourcePickers:filters.addedAt'
          : 'resourcePickers:filters.createdAt',
      ),
      type: 'date-range',
      aliases: ['created'],
    },
    {
      key: 'updatedAt',
      label: t('resourcePickers:filters.updatedAt'),
      type: 'date-range',
      aliases: ['updated'],
    },
  );
  if (scope.kind === 'store') {
    defs.push({
      key: 'publishedAt',
      label: t('resourcePickers:filters.publishedAt'),
      type: 'date-range',
      aliases: ['published'],
    });
  }
  defs.push({
    key: 'hasImage',
    label: t('resourcePickers:filters.hasImage'),
    type: 'boolean',
    ...yesNo('hasImage'),
  });

  if (scope.kind === 'org') {
    defs.push({
      key: 'scheduled',
      label: t('resourcePickers:filters.scheduled'),
      type: 'boolean',
      ...yesNo('scheduled'),
    });
  } else {
    const storeId = scope.storeId;
    const schedules = async (q: string, signal: AbortSignal) =>
      (await resourcePickersApi.schedules(storeId, q, signal)).items.map((s): FilterOption => ({
        value: s.id,
        label: s.name,
      }));
    defs.push(
      {
        key: 'scheduleState',
        label: t('resourcePickers:filters.scheduleState'),
        type: 'enum',
        aliases: ['rotation'],
        options: Object.values(SchedulePlacementStateInput).map((value) => ({
          value,
          label: t(`resourcePickers:filters.scheduleStateValue.${value}`),
        })),
      },
      {
        key: 'scheduleId',
        label: t('resourcePickers:filters.schedule'),
        type: 'async',
        multiple: false,
        aliases: ['schedule'],
        loadOptions: (q, signal) => schedules(q.trim(), signal),
        resolveOptions: async (values, signal) =>
          (await schedules('', signal)).filter((o) => values.includes(o.value)),
      },
    );
  }

  defs.push({
    key: 'fulfillmentMode',
    label: t('resourcePickers:filters.fulfillmentMode'),
    type: 'enum',
    options: Object.values(ProductListItemFulfillmentMode).map((value) => ({
      value,
      label: t(`resourcePickers:filters.fulfillmentModeValue.${value}`),
    })),
  });

  for (const attribute of attributes) {
    if (!attribute.isFilterable) continue;
    const key = attributeFilterKey(attribute.key);
    if (CHOICE_TYPES.has(attribute.dataType)) {
      defs.push({
        key,
        label: attribute.label,
        type: 'enum',
        options: [...attribute.values]
          .sort((a, b) => a.position - b.position)
          .map((value) => ({ value: value.value, label: value.label })),
      });
    } else if (attribute.dataType === 'boolean') {
      defs.push({
        key,
        label: attribute.label,
        type: 'boolean',
        trueLabel: t('resourcePickers:filters.attributeYes'),
        falseLabel: t('resourcePickers:filters.attributeNo'),
      });
    }
  }

  if (scope.kind === 'store') {
    defs.push({
      key: 'unpriced',
      label: t('resourcePickers:filters.unpriced'),
      type: 'boolean',
      ...yesNo('unpriced'),
    });
  }

  const omitted = new Set(omit);
  return defs.filter((def) => !omitted.has(def.key));
}
